import { Helmet } from 'react-helmet-async';
import { SITE_NAME, pageTitle } from '@meshsmith/shared';
import { absoluteUrl, defaultShareImage } from '../lib/site';

interface SeoProps {
  title: string;
  description?: string;
  /** the one address this page is known by; when empty it is the page's own address without any ?filters */
  canonical?: string;
  /** keep the page out of search results */
  noindex?: boolean;
  /** with noindex: still let search engines follow the links on the page (a thin tag page, say) */
  follow?: boolean;
  ogImage?: string | null;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  type?: 'website' | 'product' | 'article';
}

/**
 * Everything a search engine or a link preview (WhatsApp, Facebook, X) reads about a page: title, description, canonical
 * address, share picture and structured data. The prerendered copy of each page carries the same tags, marked data-rh so
 * that this component replaces them rather than adding a second set.
 */
export function Seo({ title, description, canonical, noindex, follow, ogImage, jsonLd, type = 'website' }: SeoProps) {
  const fullTitle = pageTitle(title);
  const url = canonical || absoluteUrl(typeof window !== 'undefined' ? window.location.pathname : '/');
  const image = ogImage || defaultShareImage();
  const robots = noindex ? (follow ? 'noindex,follow' : 'noindex,nofollow') : 'index,follow,max-image-preview:large,max-snippet:-1';
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta name="robots" content={robots} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
